const { resolveCanonicalEnv, CANONICAL_HOSTS } = require("../config/environment");

const isLocalDevHost = (hostname = "") => {
  const normalizedHostname = String(hostname).toLowerCase();

  return (
    normalizedHostname.startsWith("local") ||
    normalizedHostname.startsWith("test1") ||
    normalizedHostname.endsWith("ngrok-free.dev") ||
    normalizedHostname.endsWith("ngrok.app") ||
    normalizedHostname.endsWith("ngrok.dev")
  );
};

const isLoopbackDevHost = (hostname = "") => {
  const normalizedHostname = String(hostname).toLowerCase();

  return ["localhost", "127.0.0.1", "::1"].includes(normalizedHostname);
};

const extractConsoleSiteDomainOverride = (location = {}) => {
  const hashQuery = String(location.hash || "").includes("?")
    ? String(location.hash || "").split("?")[1]
    : "";
  const searchQuery = String(location.search || "").replace(/^\?/, "");
  const candidates = [hashQuery, searchQuery].filter(Boolean);

  for (const candidate of candidates) {
    const params = new URLSearchParams(candidate);
    const domain = params.get("domain");

    if (domain) {
      return domain;
    }
  }

  return "";
};

const resolveConsoleSiteDomain = (locationOrHostname = "") => {
  if (
    locationOrHostname &&
    typeof locationOrHostname === "object" &&
    !Array.isArray(locationOrHostname)
  ) {
    const location = locationOrHostname;
    const hostname = String(location.hostname || "").toLowerCase();
    const domainOverride = extractConsoleSiteDomainOverride(location);

    if (domainOverride) {
      return domainOverride;
    }

    return isLocalDevHost(hostname) ? "test1.onoffevent.com" : hostname;
  }

  const hostname = String(locationOrHostname || "").toLowerCase();
  return isLocalDevHost(hostname) ? "test1.onoffevent.com" : hostname;
};

const resolveConsoleApiHost = (location = {}) => {
  const { hostname = "", origin = "" } = location;

  if (isLoopbackDevHost(hostname) || isLocalDevHost(hostname)) {
    return `${origin}/__local_api__`;
  }

  // 신규 canonical(.dev/.stage.livee.kr)은 기존 livee.kr 판정보다 먼저 검사해야 한다.
  const canonicalEnv = resolveCanonicalEnv(hostname);
  if (canonicalEnv) {
    return CANONICAL_HOSTS[canonicalEnv].api;
  }

  if (hostname.endsWith("livee.kr")) {
    return "https://api.livee.kr";
  }

  if (hostname.endsWith("sharewebinar.com")) {
    return "https://api.sharewebinar.com";
  }

  return "https://api.onoffevent.com";
};

const resolveConsoleWsHost = (location = {}, encodedAuth = "") => {
  const { hostname = "", protocol = "http:", host = "" } = location;

  if (isLocalDevHost(hostname)) {
    const wsProtocol = protocol === "https:" ? "wss:" : "ws:";
    return `${wsProtocol}//${host}/__local_ws__?auth=${encodedAuth}`;
  }

  // 신규 canonical(.dev/.stage.livee.kr)은 기존 livee.kr 판정보다 먼저 검사해야 한다.
  const canonicalEnv = resolveCanonicalEnv(hostname);
  if (canonicalEnv) {
    return `${CANONICAL_HOSTS[canonicalEnv].ws}?auth=${encodedAuth}`;
  }

  if (hostname.endsWith("livee.kr")) {
    return `wss://ws-ecs.livee.kr?auth=${encodedAuth}`;
  }

  if (hostname.endsWith("sharewebinar.com")) {
    // stage 를 ECS 검증 환경으로 분리하면서 stage 전용 WS 로 교정(2026-07-29).
    // 이전에는 ws.onoffevent.com(dev WS)을 봐서 stage 화면이 dev 실시간 상태에 붙었다.
    return `wss://ws-ecs.sharewebinar.com?auth=${encodedAuth}`;
  }

  return `wss://ws-ecs.onoffevent.com?auth=${encodedAuth}`;
};

const shouldReloadOnPlayerUnmount = (hostname = "") => {
  return !isLocalDevHost(hostname);
};

const CONSOLE_AUTH_SNAPSHOT_KEY = "__consoleAuthSnapshot";

const resolveStorage = (storage = null) => {
  if (storage) {
    return storage;
  }

  // Windows Edge 등 DOM Storage 비활성(인터넷 옵션·그룹정책·쿠키 전체 차단) 환경에서는
  // window.localStorage 속성 접근 자체가 SecurityError를 던지므로 truthiness 체크도 감싼다.
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      return window.localStorage;
    }
  } catch (e) {
    return null;
  }

  return null;
};

const getStoredConsoleAuthSnapshot = (storage = null) => {
  const resolvedStorage = resolveStorage(storage);

  if (!resolvedStorage) {
    return "";
  }

  try {
    return resolvedStorage.getItem(CONSOLE_AUTH_SNAPSHOT_KEY) || "";
  } catch (e) {
    return "";
  }
};

const storeConsoleAuthSnapshot = (auth = "", storage = null) => {
  const resolvedStorage = resolveStorage(storage);

  if (!resolvedStorage) {
    return;
  }

  try {
    if (!auth) {
      resolvedStorage.removeItem(CONSOLE_AUTH_SNAPSHOT_KEY);
      return;
    }

    resolvedStorage.setItem(CONSOLE_AUTH_SNAPSHOT_KEY, auth);
  } catch (e) {
    /* DOM Storage 비활성/쿼터 초과 환경 무시 */
  }
};

const clearConsoleAuthSnapshot = (storage = null) => {
  const resolvedStorage = resolveStorage(storage);

  if (!resolvedStorage) {
    return;
  }

  try {
    resolvedStorage.removeItem(CONSOLE_AUTH_SNAPSHOT_KEY);
  } catch (e) {
    /* DOM Storage 비활성 환경 무시 */
  }
};

const matchesStoredConsoleAuth = (auth = "", storage = null) => {
  const storedAuth = getStoredConsoleAuthSnapshot(storage);

  return Boolean(auth && storedAuth && auth === storedAuth);
};

module.exports = {
  clearConsoleAuthSnapshot,
  getStoredConsoleAuthSnapshot,
  isLocalDevHost,
  isLoopbackDevHost,
  matchesStoredConsoleAuth,
  resolveConsoleApiHost,
  resolveConsoleSiteDomain,
  resolveConsoleWsHost,
  storeConsoleAuthSnapshot,
  shouldReloadOnPlayerUnmount,
};
